import type {
  FormMetadata,
  FormOptions,
  Pretty,
  // @ts-expect-error Le chemin importé n'est pas reconnu par le compilateur Typescript
} from "@conform-to/react/context";
import { useForm } from "@conform-to/react";
import { useActionData } from "react-router";
import { action } from "~/routes/auth.sign-in";
import { parseWithZod } from "@conform-to/zod";
import type { input, output, ZodErrorMap, ZodTypeAny } from "zod";
import type { Intent, Submission } from "@conform-to/dom";

/**
 * Déclare un formulaire intégré à Remix et Zod
 *
 * @param schema Le schema Zod de validation du formulaire
 * @param options Les options du formulaire Conform
 */
export function useConformingForm<
  Schema extends Record<string, unknown>,
  ZodSchema extends ZodTypeAny,
  FormValue = Schema,
  FormError = string[],
>(
  schema: ZodSchema,
  options: Pretty<
    Omit<FormOptions<Schema, FormError, FormValue>, "formId"> & {
      /**
       * The form id. If not provided, a random id will be generated.
       */
      id?: string;
      /**
       * Enable constraint validation before the dom is hydated.
       *
       * Default to `true`.
       */
      defaultNoValidate?: boolean;
    }
  > = {},
): [
  FormMetadata<Schema, FormError>,
  ReturnType<FormMetadata<Schema, FormError>["getFieldset"]>,
] {
  const lastResult = useActionData<typeof action>();
  const onValidate = ({ formData }: { formData: FormData }) =>
    parseWithZod(formData, { schema });
  return useForm({
    ...options,
    lastResult: options.lastResult ?? lastResult,
    onValidate: options.onValidate ?? onValidate,
  });
}

/**
 * Extrait et valide le formulaire contenu dans la requête donnée.
 *
 * @param request La requête contenant les données du formulaire
 * @param schema Le schéma utilisé pour valider les données
 * @param options Les options de validation des données
 */
export async function parseRequestBody<Schema extends ZodTypeAny>(
  request: Request,
  schema: Schema | ((intent: Intent | null) => Schema),
  options?: {
    async?: false;
    errorMap?: ZodErrorMap;
  },
): Promise<Submission<input<Schema>, string[], output<Schema>>> {
  const formData = await request.formData();
  return parseWithZod(formData, { schema: schema, ...options });
}
